import { FC, useCallback, useEffect, useState } from "react";
import OnboardingForm from "@components/Onboarding/OnboardingForm/OnboardingForm";
import { useTranslation } from "react-i18next";
import { FieldValues, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  OnboardingFormActionContainer,
  OnboardingFormContainer,
} from "@components/Onboarding/OnboardingForm";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useCreatePatientMutation,
  useUpdatePatientMutation,
} from "@store/patientApi";
import { Patient } from "@models/patient";
import { PatientFormType, patientToFormType } from "@utils/patientFormHelper";
import { setMessage } from "@store/snackbarSlice";
import PatientInfoForm from "@components/PatientInfoForm/PatientInfoForm";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { selectPatientId } from "@store/homeSlice";
import {
  isAddPatientFlow,
  withOnboardingFlow,
} from "@components/Onboarding/onboardingFlow";

const OnboardingPatientInfoForm: FC<{ patient?: Patient | null }> = ({
  patient,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [updatePatient] = useUpdatePatientMutation();
  const [createPatient] = useCreatePatientMutation();
  const [avatarFile, setAvatarFile] = useState<File | undefined | null>();

  const form = useForm<PatientFormType>({
    mode: "onChange",
    criteriaMode: "all",
  });
  const {
    reset,
    handleSubmit,
    clearErrors,
    formState: { isSubmitting },
  } = form;

  useEffect(() => {
    if (patient) {
      reset(patientToFormType(patient));
    }
  }, [patient, reset]);

  const save = async (values: FieldValues) => {
    try {
      const { data, error } = await (patient ? updatePatient : createPatient)({
        ...values,
        id: patient?.id,
        avatar: avatarFile as File,
      });
      if (data && !error) {
        dispatch(selectPatientId(data.id));
        navigate(withOnboardingFlow(`/onboarding/${data.id}/family-info`, location.search));
      } else {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      }
    } catch (e) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    }
  };

  const onPrevious = useCallback(() => {
    if (isAddPatientFlow(location.search)) {
      navigate("/");
      return;
    }
    navigate(withOnboardingFlow("/onboarding", location.search));
  }, [navigate, location.search]);

  return (
    <OnboardingForm
      title={t("patientInfo.title")}
      subtitle={t("patientInfo.subtitle")}
      icon={<AccountCircleIcon fontSize="small" />}
    >
      <OnboardingFormContainer
        onSubmit={(e) => {
          clearErrors();
          return handleSubmit(save)(e);
        }}
      >
        <PatientInfoForm form={form} onAvatarChange={setAvatarFile} />

        <OnboardingFormActionContainer
          isSubmitting={isSubmitting}
          onPrevious={onPrevious}
        />
      </OnboardingFormContainer>
    </OnboardingForm>
  );
};

export default OnboardingPatientInfoForm;
